import { useQuery } from "@tanstack/react-query"
import {
  discordAvatarUrlSchema,
  type MemberHome,
  pixelEventId,
  type SignupBranding,
  type SignupErrorCode,
  type SignupState,
  signupErrorCodeSchema,
} from "@ternary/contracts"
import {
  Avatar,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Stepper,
  ThemeRoot,
} from "@ternary/ui"
import {
  type FormEvent,
  type ReactElement,
  type ReactNode,
  useEffect,
  useEffectEvent,
  useId,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import { Navigate, useParams, useSearchParams } from "react-router-dom"
import { firePixelEvent, installConsentedPixels } from "../analytics.js"
import {
  confirmSignupDiscord,
  continueSignupDiscord,
  joinSignup,
  loadCustomerSession,
  loadMemberHome,
  loadSignupPage,
  loadSignupState,
  SignupApiError,
  signupDiscordPath,
} from "../api.js"
import { customerQueryKeys } from "../query-keys.js"
import { ConsentBanner } from "../signup/consent-banner.js"
import {
  guildUrlFor,
  lastSignupSlug,
  rememberGuildUrl,
  rememberSignup,
  slugFor,
} from "../signup/session.js"
import { BrandLogo, SignupLoading, SignupShell } from "../signup/shell.js"
import { type ConsentChoice, markFired, readConsent, sendJourney } from "../signup/tracking.js"
import { Icon, signupErrorHint, signupErrorMessage } from "./signup-page.js"

/**
 * A Discord avatar address only in the shape the API builds (the contract's discordAvatarUrlSchema:
 * an https cdn.discordapp.com avatar), otherwise null, so the initials show. An image from any
 * other host would tell that host the visitor's address (R69).
 */
export function discordAvatarSrc(url: string | null): string | null {
  return discordAvatarUrlSchema.safeParse(url).success ? url : null
}

function useDateFormat() {
  const { i18n } = useTranslation()
  const format = new Intl.DateTimeFormat(i18n.resolvedLanguage, { dateStyle: "medium" })
  return (iso: string) => format.format(new Date(iso))
}

/** A state of the page outside the signup's brand, which is unknown: the default theme. */
function PlainState({
  heading,
  title,
  description,
  action,
}: {
  heading: string
  title: string
  description: string
  action?: ReactNode
}) {
  return (
    <ThemeRoot className="signup-shell">
      <main className="signup-main signup-state">
        <h1 className="tny-visually-hidden">{heading}</h1>
        <EmptyState action={action} description={description} title={title} />
      </main>
    </ThemeRoot>
  )
}

function JoinUnavailable({
  error,
  signupId,
  onRetry,
}: {
  error: unknown
  signupId: string
  onRetry(): void
}) {
  const { t } = useTranslation()
  // The hold's ten minutes ran out before it was confirmed (R75 N4): A ended it, so a Retry would
  // only find nothing. Start again from this browser's own signup, or the member home.
  if (error instanceof SignupApiError && error.code === "discord_link_expired") {
    const lastSlug = lastSignupSlug()
    return (
      <PlainState
        action={
          <a
            className="tny-button tny-button-primary signup-button-link"
            href={lastSlug ? `/signup/${encodeURIComponent(lastSlug)}` : "/home"}
          >
            {t("Start again")}
          </a>
        }
        description={signupErrorMessage(t, error.code)}
        heading={t("Signup")}
        title={t("Your Discord sign-in link expired")}
      />
    )
  }
  if (error instanceof SignupApiError && error.status === 404) {
    const slug = slugFor(signupId)
    return (
      <PlainState
        action={
          slug ? (
            <a
              className="tny-button tny-button-primary signup-button-link"
              href={`/signup/${encodeURIComponent(slug)}`}
            >
              {t("Start again")}
            </a>
          ) : undefined
        }
        description={t("Check the link you followed and try again.")}
        heading={t("Signup")}
        title={t("We couldn't find this signup")}
      />
    )
  }
  return (
    <PlainState
      action={<Button onClick={onRetry}>{t("Retry")}</Button>}
      description={t("Check your connection and try again.")}
      heading={t("Signup")}
      title={t("We couldn't load your signup")}
    />
  )
}

/**
 * `/join/:signupId`, signup step 3, which also opens in a fresh browser after Discord (R55): it
 * reads everything from the signup's state. Joined, it is the member's home.
 */
export function JoinPage(): ReactElement {
  const { signupId = "" } = useParams()
  const state = useQuery({
    queryFn: ({ signal }) => loadSignupState(signupId, { signal }),
    queryKey: customerQueryKeys.signupState(signupId),
    staleTime: 0,
  })
  const slug = state.data?.slug
  const held = state.data?.step === "confirm_discord" || state.data?.step === "confirm_switch"

  // This browser now holds the signup, so /signup/<slug> resumes it and a later 404 knows the slug.
  // A held Discord account (R73) is not this browser's signup yet: it keeps its own until then.
  useEffect(() => {
    if (slug && !held) rememberSignup(slug, signupId)
  }, [held, signupId, slug])

  if (state.isPending) return <SignupLoading />
  if (state.isError) {
    return (
      <JoinUnavailable
        error={state.error}
        onRetry={() => void state.refetch()}
        signupId={signupId}
      />
    )
  }
  const data = state.data
  if (held) {
    return <JoinConfirm onLinked={() => void state.refetch()} signupId={signupId} state={data} />
  }
  if (data.step !== "join" && data.step !== "done") {
    // Not past Discord yet: the signup page resumes it at its step.
    return <Navigate replace to={`/signup/${encodeURIComponent(data.slug)}`} />
  }
  // A member who left the business's server (member tracking) gets the Join flow again.
  if (data.joinedAt && !data.rejoin) {
    const guildUrl = data.guildUrl ?? guildUrlFor(signupId)
    return (
      <MemberHomeView
        avatarUrl={data.discord?.avatarUrl ?? null}
        branding={data.branding}
        memberships={[
          {
            guildUrl,
            id: signupId,
            imageUrl: data.product.imageUrl,
            joinedAt: data.joinedAt,
            name: data.product.name,
          },
        ]}
        name={data.discord?.username ?? null}
      />
    )
  }
  return <JoinFlow onStale={() => void state.refetch()} signupId={signupId} state={data} />
}

/** The Discord account the callback linked: its avatar (Discord's CDN only) and username. */
function DiscordIdentity({
  discord,
  linked = true,
}: {
  discord: NonNullable<SignupState["discord"]>
  /** False for an account held until this browser confirms it (R73). */
  linked?: boolean
}) {
  const { t } = useTranslation()
  return (
    <div className="signup-identity">
      <Avatar name={discord.username} src={discordAvatarSrc(discord.avatarUrl)} />
      <span className="signup-identity-text">
        <span className="signup-identity-name">{discord.username}</span>
        <span className="signup-identity-note">
          {linked ? t("Your Discord account") : t("The Discord account you chose")}
        </span>
      </span>
      {linked ? <Badge tone="success">{t("Connected")}</Badge> : null}
    </div>
  )
}

/** The signup page's Privacy Policy link, for the cookie banner: the state has no legal links. */
function usePrivacyUrl(slug: string) {
  const page = useQuery({
    queryFn: ({ signal }) => loadSignupPage(slug, { signal }),
    queryKey: customerQueryKeys.signupPage(slug),
    retry: false,
  })
  return page.data?.legal.privacyUrl ?? ""
}

function JoinStepper({ current }: { current: "discord" | "join" }) {
  const { t } = useTranslation()
  return (
    <Stepper
      completedIds={current === "join" ? ["details", "discord"] : ["details"]}
      completedLabel={t("completed")}
      currentId={current}
      label={t("Signup progress")}
      stepOf={(step, total, title) =>
        t("Step {{current}} of {{total}}: {{title}}", { current: step, title, total })
      }
      steps={[
        { id: "details", label: t("Details") },
        { id: "discord", label: t("Discord") },
        { id: "join", label: t("Join") },
      ]}
    />
  )
}

// Refusals of a held link that leave nothing to try in this browser.
const HOLD_ENDED = new Set<SignupErrorCode>([
  "discord_in_use",
  "discord_link_expired",
  "too_many_attempts",
])

/**
 * The Discord account a fresh browser brought back, held until this browser confirms it (R73):
 * `confirm_discord` asks for the code emailed to the signup's address, `confirm_switch` for an
 * explicit switch from the account or signup this browser holds. Nothing links until the POST.
 * Linked (or a hold that moved on), the state is read again and the join step opens.
 */
function JoinConfirm({
  signupId,
  state,
  onLinked,
}: {
  signupId: string
  state: SignupState
  onLinked(): void
}) {
  const { t } = useTranslation()
  const titleId = useId()
  const switching = state.step === "confirm_switch"
  const privacyUrl = usePrivacyUrl(state.slug)
  const session = useQuery({
    enabled: switching,
    queryFn: ({ signal }) => loadCustomerSession({ signal }),
    queryKey: customerQueryKeys.session,
    retry: false,
  })
  const [code, setCode] = useState("")
  const [emptyCode, setEmptyCode] = useState(false)
  const [pending, setPending] = useState(false)
  const pendingRef = useRef(false)
  const [failure, setFailure] = useState<unknown>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const alertRef = useRef<HTMLParagraphElement>(null)
  const primaryRef = useRef<HTMLButtonElement>(null)
  const company = state.branding.companyName
  const next = state.discord?.username ?? ""

  const refusal = failure instanceof SignupApiError ? failure : null
  const errorCode = refusal?.code ?? null
  const ended =
    (errorCode !== null && HOLD_ENDED.has(errorCode)) ||
    (errorCode === "code_invalid" && refusal?.attemptsRemaining === 0)
  const copy = failure
    ? [
        signupErrorMessage(t, errorCode, {
          attemptsRemaining: refusal?.attemptsRemaining ?? null,
          during: "confirm",
        }),
        signupErrorHint(t, errorCode, company),
      ]
        .filter(Boolean)
        .join(" ")
    : ""
  // A wrong or malformed code belongs to the code field (the switch hold has none); anything else is the step's alert.
  const onField =
    !switching && !ended && (errorCode === "code_invalid" || errorCode === "invalid_request")
  const fieldError = emptyCode ? t("Enter the 6-digit code.") : onField ? copy : undefined

  useEffect(() => {
    if (ended) alertRef.current?.focus()
    else if (fieldError) inputRef.current?.focus()
  }, [ended, fieldError])

  const send = async (request: () => Promise<unknown>) => {
    if (pendingRef.current) return
    pendingRef.current = true
    setPending(true)
    setFailure(null)
    try {
      await request()
      // Stays busy: the state read that follows opens the join step.
      onLinked()
    } catch (error) {
      pendingRef.current = false
      setPending(false)
      // A newer Discord return replaced this hold: the state says where the signup is now.
      if (error instanceof SignupApiError && error.code === "wrong_step") onLinked()
      else setFailure(error)
    }
  }

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (switching) {
      // Not before the page has said who is signed in now (a failed read waits for its Retry).
      if (!session.isSuccess) return
      void send(() => continueSignupDiscord(signupId))
      return
    }
    if (!/^\d{6}$/.test(code)) {
      setFailure(null)
      setEmptyCode(true)
      return
    }
    setEmptyCode(false)
    void send(() => confirmSignupDiscord(signupId, code))
  }

  if (refusal?.status === 404) {
    return <JoinUnavailable error={refusal} onRetry={onLinked} signupId={signupId} />
  }

  const signedInAs = session.data?.authenticated ? session.data.viewer.displayName : null
  const lastSlug = lastSignupSlug()
  // Cancel is a plain navigation: the session or signup this browser holds stays as it was. With
  // the session unknown (a failed read), it goes where a browser with no session would.
  const cancelHref = signedInAs || !lastSlug ? "/home" : `/signup/${encodeURIComponent(lastSlug)}`
  const banner = (
    <ConsentBanner
      onChoice={() =>
        (ended ? alertRef.current : (inputRef.current ?? primaryRef.current))?.focus()
      }
      privacyUrl={privacyUrl}
    />
  )

  return (
    <SignupShell banner={banner} page={state}>
      <JoinStepper current="discord" />
      <form aria-labelledby={titleId} className="signup-join" noValidate onSubmit={submit}>
        <h1 className="signup-title" id={titleId}>
          {switching ? t("Switch Discord accounts?") : t("Confirm it's you")}
        </h1>
        <p className="signup-lead">
          {switching
            ? session.isPending
              ? null
              : session.isError
                ? t("Continue with this Discord account?")
                : signedInAs
                  ? t("You're signed in as {{current}}. Continue as {{next}}?", {
                      current: signedInAs,
                      next,
                    })
                  : t(
                      "This replaces the signup in progress in this browser. Continue as {{next}}?",
                      {
                        next,
                      },
                    )
            : t("Enter the code we emailed to {{email}}.", { email: state.email ?? "" })}
        </p>
        {state.discord ? <DiscordIdentity discord={state.discord} linked={false} /> : null}
        {failure && !onField ? (
          <p className="tny-error" ref={alertRef} role="alert" tabIndex={-1}>
            {copy}
          </p>
        ) : null}
        {switching && session.isError ? (
          <Button onClick={() => void session.refetch()} variant="secondary">
            {t("Retry")}
          </Button>
        ) : null}
        {ended ? null : switching ? (
          <Button disabled={!session.isSuccess} loading={pending} ref={primaryRef} type="submit">
            {t("Continue as {{name}}", { name: next })}
          </Button>
        ) : (
          <>
            <Field
              error={fieldError}
              hint={t("It works for 10 minutes.")}
              label={t("6-digit code")}
              required
            >
              <Input
                autoComplete="one-time-code"
                className="signup-code-input"
                inputMode="numeric"
                onChange={(event) =>
                  setCode(event.currentTarget.value.replace(/\D/g, "").slice(0, 6))
                }
                ref={inputRef}
                value={code}
              />
            </Field>
            <Button loading={pending} ref={primaryRef} type="submit">
              {t("Confirm")}
            </Button>
          </>
        )}
        {switching ? (
          <a className="tny-button tny-button-secondary signup-button-link" href={cancelHref}>
            {t("Cancel")}
          </a>
        ) : null}
      </form>
    </SignupShell>
  )
}

function JoinFlow({
  signupId,
  state,
  onStale,
}: {
  signupId: string
  state: SignupState
  /** Reads the state again, after a join that lost to a parallel one (wrong_step). */
  onStale(): void
}) {
  const { t } = useTranslation()
  const titleId = useId()
  const [searchParams] = useSearchParams()
  // A re-link's return, which the signup page forwards here (I2).
  const linkError = signupErrorCodeSchema.safeParse(searchParams.get("error"))
  const [joining, setJoining] = useState(false)
  const joiningRef = useRef(false)
  const [failure, setFailure] = useState<unknown>(null)
  const [guildUrl, setGuildUrl] = useState<string | null>(null)
  const doneRef = useRef<HTMLHeadingElement>(null)
  const primaryRef = useRef<HTMLButtonElement>(null)
  const relinkRef = useRef<HTMLAnchorElement>(null)
  const openRef = useRef<HTMLAnchorElement>(null)
  const viewed = useRef(false)
  const pageViewed = useRef(false)
  const company = state.branding.companyName
  const product = state.product.name
  // The state carries the brand; the page only the banner's link. A failure (an archived
  // product's 404, say) leaves the banner without it.
  const privacyUrl = usePrivacyUrl(state.slug)

  const firePageView = () => {
    if (pageViewed.current) return
    pageViewed.current = true
    installConsentedPixels(state.pixels)
    firePixelEvent(state.pixels, { name: "PageView", path: window.location.pathname })
  }

  // At most once per signup on this device, and only a consenting visitor uses up the mark.
  const fireCompleteRegistration = () => {
    if (readConsent()?.ads !== true) return
    installConsentedPixels(state.pixels)
    const eventId = pixelEventId(signupId, "CompleteRegistration")
    if (markFired(eventId)) firePixelEvent(state.pixels, { eventId, name: "CompleteRegistration" })
  }

  // Once per load: the step view, and a page view for a visitor who accepted ad cookies earlier.
  const onLoad = useEffectEvent(() => {
    sendJourney({ event: "step_view", signupId, step: "join" })
    if (readConsent()?.ads === true) firePageView()
  })
  useEffect(() => {
    if (viewed.current) return
    viewed.current = true
    onLoad()
  }, [])

  useEffect(() => {
    if (guildUrl) doneRef.current?.focus()
  }, [guildUrl])

  // The focused Join button gives way to Connect Discord, so focus moves to it (m4).
  const notLinked = failure instanceof SignupApiError && failure.code === "discord_not_linked"
  useEffect(() => {
    if (notLinked) relinkRef.current?.focus()
  }, [notLinked])

  // A browser that never answered (R55's fresh browser) answers here (R72). Accept counts this
  // page, and a join this load already made. The clicked button is gone, so focus moves on.
  const onConsent = (choice: ConsentChoice) => {
    if (choice.ads) {
      firePageView()
      if (guildUrl) fireCompleteRegistration()
    }
    ;(guildUrl ? openRef.current : (relinkRef.current ?? primaryRef.current))?.focus()
  }
  const banner = <ConsentBanner onChoice={onConsent} privacyUrl={privacyUrl} />

  const join = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (joiningRef.current) return
    joiningRef.current = true
    setJoining(true)
    setFailure(null)
    try {
      const joined = await joinSignup(signupId)
      rememberGuildUrl(signupId, joined.guildUrl)
      setGuildUrl(joined.guildUrl)
      try {
        fireCompleteRegistration()
      } catch {
        // The member is in: a pixel never turns that into a failed join.
      }
    } catch (error) {
      // A parallel join won before this one finished: the state says where the signup is now.
      if (error instanceof SignupApiError && error.code === "wrong_step") onStale()
      else setFailure(error)
    } finally {
      joiningRef.current = false
      setJoining(false)
    }
  }

  if (guildUrl) {
    return (
      <SignupShell banner={banner} page={state}>
        <div className="signup-done">
          <span aria-hidden="true" className="signup-done-mark">
            <Icon name="check" />
          </span>
          <h1 className="signup-title" ref={doneRef} tabIndex={-1}>
            {t("You're in!")}
          </h1>
          <p className="signup-lead">
            {t("Welcome to {{product}}. You're now in the {{company}} Discord server.", {
              company,
              product,
            })}
          </p>
          <div className="signup-actions">
            <a
              className="tny-button tny-button-primary signup-button-link"
              href={guildUrl}
              ref={openRef}
              rel="noreferrer"
              target="_blank"
            >
              {t("Open Discord", { context: "server" })}
              <Icon name="external" />
            </a>
            <a className="tny-button tny-button-secondary signup-button-link" href="/home">
              {t("Go to your member home")}
            </a>
          </div>
        </div>
      </SignupShell>
    )
  }

  // The cookie no longer names this signup: the same view as a /join link that was never held.
  if (failure instanceof SignupApiError && failure.status === 404) {
    return <JoinUnavailable error={failure} onRetry={() => undefined} signupId={signupId} />
  }

  const code = failure instanceof SignupApiError ? failure.code : null
  // Not linked (an expired grant, say): only Connect Discord helps, so it replaces Try again.
  const relink =
    code === "discord_not_linked"
      ? ((failure as SignupApiError).authorizationUrl ?? signupDiscordPath(signupId))
      : null
  const shownError = failure ? null : linkError.success ? linkError.data : null
  return (
    <SignupShell banner={banner} page={state}>
      <JoinStepper current="join" />
      <form aria-labelledby={titleId} className="signup-join" noValidate onSubmit={join}>
        <h1 className="signup-title" id={titleId}>
          {t("Join {{product}}", { product })}
        </h1>
        <p className="signup-lead">
          {t("One step left: we'll add you to the {{company}} Discord server.", { company })}
        </p>
        {shownError ? (
          <p className="tny-error" role="alert">
            {[signupErrorMessage(t, shownError), signupErrorHint(t, shownError, company)]
              .filter(Boolean)
              .join(" ")}
          </p>
        ) : null}
        {state.discord ? <DiscordIdentity discord={state.discord} /> : null}
        {failure ? (
          <p className="tny-error" role="alert">
            {[signupErrorMessage(t, code), signupErrorHint(t, code, company)]
              .filter(Boolean)
              .join(" ")}
          </p>
        ) : null}
        {relink ? (
          <a
            className="tny-button tny-button-primary signup-button-link"
            href={relink}
            ref={relinkRef}
          >
            {t("Connect Discord")}
          </a>
        ) : (
          <>
            <Button loading={joining} ref={primaryRef} type="submit">
              {failure ? t("Try again") : t("Join the Discord server")}
            </Button>
            {/* After a re-link that did not finish, this is the retry, so it is not a quiet link. */}
            <a
              className={
                shownError
                  ? "tny-button tny-button-secondary signup-button-link"
                  : "tny-button tny-button-ghost tny-button-sm signup-button-link signup-other-account"
              }
              href={signupDiscordPath(signupId)}
            >
              {t("Use a different Discord account")}
            </a>
          </>
        )}
      </form>
    </SignupShell>
  )
}

/** A member home product; a joined signup's own may not know its guild URL yet. */
type Membership = Omit<MemberHome["products"][number], "guildUrl"> & { guildUrl: string | null }

/** A product's image on its card, or nothing when it has none or it fails to load. */
function MembershipImage({ src }: { src: string | null }) {
  const [failed, setFailed] = useState<string | null>(null)
  if (!src || src === failed) return null
  // The card's title names the product, so the image itself is decorative.
  return <img alt="" className="member-thumb" onError={() => setFailed(src)} src={src} />
}

/**
 * The member's home, in the tenant's brand: who is signed in, and each product joined with its
 * date and Open Discord. `/home` lists every product; a joined `/join/:signupId` lists its own.
 */
function MemberHomeView({
  branding,
  name,
  avatarUrl,
  memberships,
}: {
  branding: SignupBranding
  name: string | null
  avatarUrl: string | null
  memberships: Membership[]
}) {
  const { t } = useTranslation()
  const formatDate = useDateFormat()
  const headingId = useId()
  const company = branding.companyName

  useEffect(() => {
    document.title = t("Your memberships · {{company}}", { company })
  }, [company, t])

  return (
    <ThemeRoot brand={branding.primaryColor} className="member-shell" mode={branding.theme}>
      <header className="member-bar">
        <span className="member-brand">
          <BrandLogo name={company} src={branding.logoUrl} />
          <span className="signup-brand-name">{company}</span>
        </span>
        {name ? (
          <span className="member-who">
            <Avatar name={name} size="sm" src={discordAvatarSrc(avatarUrl)} />
            <span className="member-name">{name}</span>
          </span>
        ) : null}
      </header>
      <main className="member-main">
        <div className="member-intro">
          <h1 className="signup-title">
            {name ? t("Welcome back, {{username}}", { username: name }) : t("Welcome back")}
          </h1>
          <p className="member-muted">{t("You're signed in with Discord.")}</p>
        </div>
        <section aria-labelledby={headingId} className="member-section">
          <h2 className="member-heading" id={headingId}>
            {t("Your memberships")}
          </h2>
          {memberships.length > 0 ? (
            <ul className="member-grid">
              {memberships.map((membership) => (
                <li key={membership.id}>
                  <Card headingLevel={3} title={membership.name}>
                    <div className="member-card-body">
                      <MembershipImage src={membership.imageUrl} />
                      <p className="member-muted">
                        {t("Member since {{date}}", { date: formatDate(membership.joinedAt) })}
                      </p>
                      {membership.guildUrl ? (
                        <a
                          className="tny-button tny-button-primary tny-button-sm signup-button-link"
                          href={membership.guildUrl}
                          rel="noreferrer"
                          target="_blank"
                        >
                          {t("Open Discord", { context: "server" })}
                          <Icon name="external" />
                        </a>
                      ) : null}
                    </div>
                  </Card>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              description={t("When you join a {{company}} community, it shows here.", { company })}
              title={t("No memberships yet")}
            />
          )}
        </section>
      </main>
    </ThemeRoot>
  )
}

/** `/home`, the member home: every product the signed-in Discord account joined. */
export function MemberHomePage(): ReactElement {
  const { t } = useTranslation()
  const home = useQuery({
    queryFn: ({ signal }) => loadMemberHome({ signal }),
    queryKey: customerQueryKeys.memberHome,
    staleTime: 0,
  })
  if (home.isPending) return <SignupLoading label={t("Loading your memberships")} />
  if (home.isError) {
    const error = home.error
    if (error instanceof SignupApiError && error.status === 401) {
      return (
        <PlainState
          action={
            error.authorizationUrl ? (
              <a
                className="tny-button tny-button-primary signup-button-link"
                href={error.authorizationUrl}
              >
                {t("Log in with Discord")}
              </a>
            ) : undefined
          }
          description={t("Log in with Discord to continue.")}
          heading={t("Your memberships")}
          title={t("Log in to see your memberships")}
        />
      )
    }
    return (
      <PlainState
        action={<Button onClick={() => void home.refetch()}>{t("Retry")}</Button>}
        description={t("Check your connection and try again.")}
        heading={t("Your memberships")}
        title={t("We couldn't load your memberships")}
      />
    )
  }
  return (
    <MemberHomeView
      avatarUrl={home.data.avatarUrl}
      branding={home.data.branding}
      memberships={home.data.products}
      name={home.data.displayName}
    />
  )
}

/**
 * `/link-expired` (R77; customer-api owns `GET /login`), where A sends a Discord return whose
 * sign-in state was forged, replayed or older than 10 minutes (`?error=invalid-oauth-state`).
 * "Start again" goes back to the signup this browser held last, or else to the member home,
 * which offers a new Discord login (m11).
 */
export function LinkExpiredPage(): ReactElement {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const expired = searchParams.get("error") === "invalid-oauth-state"
  const slug = lastSignupSlug()
  return (
    <PlainState
      action={
        <a
          className="tny-button tny-button-primary signup-button-link"
          href={slug ? `/signup/${encodeURIComponent(slug)}` : "/home"}
        >
          {t("Start again")}
        </a>
      }
      description={
        expired
          ? t("A sign-in link works once, for 10 minutes. Start again to get a new one.")
          : t("Start again to sign in with Discord.")
      }
      heading={t("Sign in")}
      title={expired ? t("Your Discord sign-in link expired") : t("We couldn't sign you in")}
    />
  )
}
